import React from 'react';
import EditIcon from '@mui/icons-material/Edit';
import PreviewIcon from '@mui/icons-material/Preview';
import ToggleOffIcon from '@mui/icons-material/ToggleOff';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import VisibilityIcon from '@mui/icons-material/Visibility';

export const FORMS_PAGE_SIZE = 10;

export const FORMS_COLUMNS = [
  {
    name: 'name',
    displayName: 'Name',
    sortOrder: 'none',
    isFilterable: true,
    isSortable: true,
    isVisible: true,
    type: 'value',
  },
  {
    name: 'createdBy',
    displayName: 'Created by',
    sortOrder: 'none',
    isFilterable: true,
    isSortable: true,
    isVisible: true,
    type: 'value',
  },
  {
    name: 'active',
    displayName: 'Active',
    sortOrder: 'none',
    isFilterable: true,
    isSortable: true,
    isVisible: true,
    type: 'value',
  },
  {
    name: 'submissionCount',
    displayName: 'Submission count',
    sortOrder: 'none',
    isFilterable: true,
    isSortable: true,
    isVisible: true,
    type: 'value',
  },
];

const NAVY_ICON_SX = { color: 'var(--navy-main)', fontSize: 24 };
const NAVY_ACTION_STYLE = { color: 'var(--navy-main)' };

const formatCreatedByDisplay = (form) => {
  const cb = form?.createdBy;
  if (cb == null) {
    return '';
  }
  const first = (cb.firstName ?? '').trim();
  const last = (cb.lastName ?? '').trim();
  const full = `${first} ${last}`.trim();
  if (full !== '') {
    return full;
  }
  if (cb.username != null && String(cb.username).trim() !== '') {
    return String(cb.username).trim();
  }
  return '';
};

const canDisableForm = (form, currentPersonId) =>
  currentPersonId != null &&
  form.personKey != null &&
  Number(form.personKey) === Number(currentPersonId);

const buildDisableAction = (form, onToggleActive) => ({
  title: 'Disable form',
  onClick: () => onToggleActive(form),
  id: 'toggle-active',
  icon: <ToggleOffIcon sx={NAVY_ICON_SX} />,
  style: NAVY_ACTION_STYLE,
});

const buildEnableAction = (form, onToggleActive) => ({
  title: 'Enable form',
  onClick: () => onToggleActive(form),
  id: 'toggle-active',
  icon: <ToggleOnIcon sx={NAVY_ICON_SX} />,
  style: NAVY_ACTION_STYLE,
});

const buildViewSubmissionsAction = (form, onViewSubmissions) => ({
  title: 'View form submission',
  onClick: () => onViewSubmissions(form),
  id: 'view-submissions',
  icon: <VisibilityIcon sx={NAVY_ICON_SX} />,
  style: NAVY_ACTION_STYLE,
});

export const buildPreviewFormAction = (form, onPreviewForm) => ({
  title: 'Preview form',
  onClick: () => onPreviewForm(form),
  id: 'preview-form',
  icon: <PreviewIcon sx={NAVY_ICON_SX} />,
  style: NAVY_ACTION_STYLE,
});

export const buildEditAction = (form, onEditForm) => ({
  title: 'Edit form',
  onClick: () => onEditForm(form),
  id: 'edit-form',
  icon: <EditIcon sx={NAVY_ICON_SX} />,
  style: NAVY_ACTION_STYLE,
});

export const buildToggleAction = (form, onToggleActive, currentPersonId) => {
  if (!form.active) {
    return buildEnableAction(form, onToggleActive);
  }
  return canDisableForm(form, currentPersonId)
    ? buildDisableAction(form, onToggleActive)
    : null;
};

/**
 * @param {object} form
 * @param {object} handlers
 * @param {number|undefined} currentPersonId — logged-in staff person id (`USER_CONTEXT.personId`)
 * @param {function(object): void} handlers.onToggleActive
 * @param {function(object): void} handlers.onPreviewForm
 * @param {function(object): void} handlers.onViewSubmissions
 * @param {function(object): void} handlers.onEditForm
 */
const getFormRowActions = (
  form,
  { onToggleActive, onPreviewForm, onViewSubmissions, onEditForm },
  currentPersonId,
) => {
  const toggleAction = buildToggleAction(form, onToggleActive, currentPersonId);
  return [
    ...(toggleAction ? [toggleAction] : []),
    buildPreviewFormAction(form, onPreviewForm),
    buildViewSubmissionsAction(form, onViewSubmissions),
    buildEditAction(form, onEditForm),
  ];
};

/**
 * @param {object[]} forms
 * @param {object} actionHandlers
 * @param {number|undefined} currentPersonId
 * @param {function(object): void} actionHandlers.onToggleActive
 * @param {function(object): void} actionHandlers.onPreviewForm
 * @param {function(object): void} actionHandlers.onViewSubmissions
 * @param {function(object): void} actionHandlers.onEditForm
 */
export const mapFormsToGridData = (forms, actionHandlers, currentPersonId) =>
  forms.map((form) => ({
    isSelected: false,
    rowData: {
      name: form.name,
      createdBy: formatCreatedByDisplay(form),
      active: form.active ? 'Yes' : 'No',
      submissionCount: String(form.submissionCount ?? 0),
    },
    preDefineComponentProps: {
      actions: getFormRowActions(form, actionHandlers, currentPersonId),
    },
  }));
